<template>
  <div class="management-fee-amount">
    <p :class="[{'required-flag': canUpdate}, 'blod-p']">{{ $t('tenantProjects.managementFeeAmount') }}</p>
    <div class="btns">
      <button
        class="btn btn--darkblue"
        @click="notifyPM"
        v-if="canUpdate && !canNotifyAccounting"
      >{{ $t('tenantProjects.notifyPM') }}</button>
      <button
        class="btn btn--darkblue margin-left-15"
        @click="notifyAccounting"
        v-if="canNotifyAccounting"
      >{{ $t('tenantProjects.notifyAccounting') }}</button>
      <div
        v-if="isEngineer"
        class="management-fee-notify-info"
      >
        <i
          v-if="notifyPMInfo && canRead"
          class="gray-font"
        >{{ $t('tenantProjects.notifyPMByAt', [notifyPMInfo.by, notifyPMInfo.time]) }}</i>
        <i
          v-if="notifyAccountingInfo"
          class="gray-font"
        >{{ $t('tenantProjects.notifyAccountingByAt', [notifyAccountingInfo.by, notifyAccountingInfo.time]) }}</i>
      </div>
    </div>
    <div v-if="isEngineer">
      <div class="choose-management-fee-amount">
        <input
          type="radio"
          checked
          :disabled="!canUpdate"
        >
        <label class="management-fee-amount-label">{{ $t('tenantProjects.managementFeeAmount') }} </label>
        <label class="management-fee-amount-label currency-prefix">{{ $globalProps.getCurrency() }}</label>
        <InputField
          class="input-management-fee-amount"
          :readonly="!canUpdate && !isEditingAmount"
          :showColon="false"
          :placeholder="'0.00'"
          :showFlag="false"
          :value="amount"
          :showEmptyDash="true"
          :readonlyAsInput="(isNotifiedPM && !isEditingAmount)"
          :inputTip="hintString"
          :inputCheckType="'decimal'"
          :pattern="regex.decimal"
          :parentNode="FORM_SRC_PROJECT_CONTROL"
          @updateValue="val => updateAmount(val)"
          :required="true"
        />
        <i
          v-show="isNotifiedPM && !isEditingAmount && canUpdate"
          @click="isEditingAmount = true"
          class="fa fa-pencil"
        />
      </div>
    </div>
    <div v-else-if="isUserNotifyAccounting">
      <label class="medium-size margin-right-15">{{ $globalProps.getCurrency() + ' ' }} <span class="blod-p">{{ showAmount }}</span></label>
    </div>
    <div v-else>
      {{ $t('emptyValue') }}
    </div>

    <Modal
      ref="management-fee-input-modal"
      modalType="small-modal"
      class="management-fee-input-modal"
      :showCloseBtn="false"
      :modalId="`management-fee-input-modal_${tenantProjectId}`"
    >
      <p
        slot="content"
        class="font-bold"
      >{{ $t('tenantProjects.inputAccountingHint') + ' ' }}
        <span>{{ accountingEmail || '--' }}</span>
      </p>
      <div slot="btns">
        <button
          class="btn--darkblue"
          @click="closeModal"
        >{{ $t('action.cancel') }}</button>
        <button
          class="btn--orange"
          @click="sendEmailToAccounting"
        >{{ $t('action.send') }}</button>
      </div>
    </Modal>
  </div>
</template>

<script>
import CurrencyFormatter from 'currency-formatter'
import lodash from 'lodash'
import { mapGetters, mapActions } from 'vuex'

import dateUtil from '@/utils/dateUtil'
import InputField from '@/controls/InputField'
import Modal from '@/controls/Modal'
import * as regex from '@/utils/regex'
import * as tenantProjectApi from '@/api/tenantProjects/request'
import { DEPOSIT_HISTORY_TYPE } from '../deposit/consts'
import { EmailTypes } from '@/utils/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getRegionEnvsByCode } from '@/api/settings/request'
import { sendEmail } from '@/api/postman/request'
import { utils as formUtils } from '@/plugin/InputChange'
import notification from '@/utils/notification'
import i18n from '@/utils/i18n'
import { validateProjectControlFieldsOnLoad } from '../fieldValidationOnLoad'
import { FORM_SRC_PROJECT_CONTROL } from '../consts'
import { buildDirtyFieldSaveRequest, getModifiedSaveFields, initTenantProjectDepositSync, isPersistableDepositRefundAmount, normalizeDepositAmount, pickSaveFieldValues, reloadTenantProjectOnDepositSync, syncManagementFeeNotifyAccounting, syncNotifyPm, syncSaveDepositAmount, validateModifiedSaveFields } from '../deposit/sync.service'
import { TENANT_PROJECT_DEPOSIT_SYNC_ACTION } from '../deposit/sync'

const MANAGEMENT_SAVE_FIELDS = ['amount']

const MANAGEMENT_SAVE_FIELD_VALIDATORS = {
  amount: {
    inputSelector: '.input-management-fee-amount',
    validSelector: '.management-fee-amount',
  },
}

export default {
  name: 'ManagementFeeAmount',
  props: {
    canUpdate: Boolean,
    canNotifyAccounting: Boolean,
    canRead: Boolean,
    chequeFiles: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      isEditingAmount: false,
      amount: '',
      regex,
      accountingEmail: '',
      tenantProjectId: '',
      hintString: this.$t('hint.inputTip.string'),
      FORM_SRC_PROJECT_CONTROL,
    }
  },
  components: {
    InputField,
    Modal,
  },
  computed: {
    ...mapGetters('user', ['isTenant', 'currentUser', 'isEngineer']),
    ...mapGetters('tenantProject', ['currentTenantProject', 'management']),
    showAmount () {
      let showValue = this.$t('emptyValue')
      const actionHistory = lodash.get(this.management, 'actionHistory', [])
      const lastAmount = actionHistory.find(history => history.actionType === 'notifyAccounting')
      if (lastAmount && lodash.isNumber(parseFloat(lastAmount.amount))) {
        showValue = CurrencyFormatter.format(lastAmount.amount, 'USD')
      }
      return showValue
    },
    isNotifiedPM () {
      return !!lodash.get(this, 'notifyPMInfo.time')
    },
    notifyPMInfo () {
      return this.getNotifyInfo('notifyPM')
    },
    notifyAccountingInfo () {
      return this.getNotifyInfo('notifyAccounting')
    },
    isUserNotifyAccounting () {
      return !!this.notifyAccountingInfo
    },
    isShowUpdateTime () {
      if (this.isTenant && !this.notifyAccountingInfo) {
        return null
      } else {
        return this.management && this.management.updatedAt
      }
    },
  },
  watch: {
    management: {
      handler () {
        this.amount = lodash.get(this.management, 'amount')
        this.showFieldValidationErrors()
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('tenantProject', ['loadTenantProject']),
    updateAmount (val) {
      this.amount = val
      this.scheduleDirtyStateCheck()
    },
    scheduleDirtyStateCheck () {
      this.$nextTick(() => {
        window.setTimeout(() => {
          this.$emit('dirty-state-change')
        }, 0)
      })
    },
    showFieldValidationErrors () {
      this.$nextTick(() => {
        // Tab selection via EventBus runs after ~100ms; wait so inputs are visible before showing tooltips.
        setTimeout(() => {
          if (!(this.management && this.management.updatedAt) || !this.isEngineer) {
            return
          }
          validateProjectControlFieldsOnLoad('.input-management-fee-amount')
        }, 150)
      })
    },
    validate () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.input-management-fee-amount')
      if (!this.validateAmountBeforeSave()) {
        return false
      }
      return fusionInputCheckUtil.isAllFieldsValid('.management-fee-amount')
    },
    validateAmountBeforeSave () {
      if (isPersistableDepositRefundAmount(this.amount)) {
        return true
      }
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.input-management-fee-amount')
      notification.error(this.$t('tenantProjects.correctAmountBeforeSave'))
      return false
    },
    notifyAccounting () {
      if (this.validate()) {
        this.openModal()
      }
    },
    closeModal () {
      this.accountingEmail = ''
      this.$refs['management-fee-input-modal'].close()
    },
    async openModal () {
      this.accountingEmail = await getRegionEnvsByCode('mail.managementAccountingMail')
      const isEmptyObject = typeof this.accountingEmail === 'object' && !Object.keys(this.accountingEmail).length
      if (!this.accountingEmail || isEmptyObject) {
        notification.error(i18n.t('error.getAccountingEmail.system'))
        return
      }
      this.$refs['management-fee-input-modal'].open()
    },
    async uploadManagementInfo (type) {
      const amount = normalizeDepositAmount(this.amount)
      const request = {
        amount,
        updatedBy: lodash.get(this, 'management.updator.id'),
      }
      if (this.isModified()) {
        request.updatedAt = new Date()
        request.updatedBy = this.currentUser.id
      }
      const actionHistory = lodash.cloneDeep(this.management.actionHistory)
      actionHistory.unshift({
        actionType: type,
        user: {
          id: this.currentUser.id,
          username: this.currentUser.username,
          displayName: this.currentUser.displayName,
        },
        amount,
        isNewRec: true,
      })
      request.actionHistory = actionHistory
      const result = await tenantProjectApi.updateDepositRefund(this.management.id, request)
      if (!result) {
        return null
      }
      await this.loadTenantProject(this.tenantProjectId)
      return result
    },
    async saveAndContinue ({ deferFinalize = false } = {}) {
      const result = await syncSaveDepositAmount({
        validate: this.validateAmountBeforeSave,
        uploadSaveInfo: () => this.uploadSaveAndContinueInfo({ skipReload: deferFinalize }),
        hasDirtySaveFields: () => this.hasDirtySaveFields(),
        resetDirty: deferFinalize ? () => undefined : () => formUtils.resetDirty(FORM_SRC_PROJECT_CONTROL),
      })
      if (result) {
        this.isEditingAmount = false
      }
      return result
    },
    getSaveFieldValues () {
      return pickSaveFieldValues(MANAGEMENT_SAVE_FIELDS, this, {
        amount: normalizeDepositAmount,
      })
    },
    hasDirtySaveFields () {
      return this.getModifiedSaveFieldsForSave().length > 0
    },
    getModifiedSaveFieldsForSave () {
      return getModifiedSaveFields(this.management, this.getSaveFieldValues(), MANAGEMENT_SAVE_FIELDS)
    },
    validateSaveAndContinue () {
      if (!this.validateAmountBeforeSave()) {
        return false
      }
      return validateModifiedSaveFields({
        record: this.management,
        local: this.getSaveFieldValues(),
        fields: MANAGEMENT_SAVE_FIELDS,
        fieldValidators: MANAGEMENT_SAVE_FIELD_VALIDATORS,
        context: this,
      })
    },
    async uploadSaveAndContinueInfo ({ skipReload = false } = {}) {
      const request = buildDirtyFieldSaveRequest({
        record: this.management,
        local: this.getSaveFieldValues(),
        fields: MANAGEMENT_SAVE_FIELDS,
        currentUser: this.currentUser,
        actionType: DEPOSIT_HISTORY_TYPE.saveDepositAmount,
      })
      if (!request) {
        return true
      }
      const result = await tenantProjectApi.updateDepositRefund(this.management.id, request)
      if (!result) {
        return null
      }
      if (!skipReload) {
        await this.loadTenantProject(this.tenantProjectId)
      }
      return result
    },
    async notifyPM () {
      this.isEditingAmount = false
      await syncNotifyPm({
        validate: this.validate,
        uploadInfo: this.uploadManagementInfo,
        notifyType: DEPOSIT_HISTORY_TYPE.notifyPM,
        resetDirty: () => formUtils.resetDirty(FORM_SRC_PROJECT_CONTROL),
        sendEmail,
        emailType: EmailTypes['tenantProjects'].managementFeeNotifyPM,
        tenantProjectId: this.tenantProjectId,
        syncAction: TENANT_PROJECT_DEPOSIT_SYNC_ACTION.managementFeeNotifyPM,
      })
    },
    async sendEmailToAccounting () {
      this.isEditingAmount = false
      await syncManagementFeeNotifyAccounting({
        uploadManagementInfo: this.uploadManagementInfo,
        resetDirty: () => formUtils.resetDirty(FORM_SRC_PROJECT_CONTROL),
        sendEmail,
        closeModal: this.closeModal,
        tenantProjectId: this.tenantProjectId,
        emailType: EmailTypes['tenantProjects'].managementFeeNotifyAccounting,
        email: this.accountingEmail,
      })
    },
    async handleManagementFeeSync (event) {
      await reloadTenantProjectOnDepositSync({
        event,
        tenantProjectId: this.tenantProjectId,
        loadTenantProject: this.loadTenantProject,
      })
    },
    isModified () {
      return this.management.amount !== this.amount
    },
    getNotifyInfo (type) {
      const actionHistory = lodash.get(this.management, 'actionHistory', [])
      let info = ''
      for (let i = 0, len = actionHistory.length; i < len; i++) {
        if (actionHistory[i].actionType === type) {
          info = {
            by: actionHistory[i].user.displayName,
            time: dateUtil.formatDate(actionHistory[i].updatedAt, true),
          }
          break
        }
      }
      return info
    },
  },
  mounted () {
    initTenantProjectDepositSync({
      getBuildingFloorZoneMap: async () => ({ BuildingFloorZoneMap: {} }),
      routeId: this.$route.params.id,
      addStorageListener: () => {
        window.addEventListener('storage', this.handleManagementFeeSync)
      },
    }).then(({ tenantProjectId }) => {
      this.tenantProjectId = tenantProjectId
      this.amount = lodash.get(this.management, 'amount')
      this.showFieldValidationErrors()
    })
  },
  beforeDestroy () {
    window.removeEventListener('storage', this.handleManagementFeeSync)
  },
}
</script>

<style lang='scss' scoped>
@import '~styles/settings';

.management-fee-amount {
  position: relative;
 .management-fee-amount-label{
   font-weight: normal
 }
  .currency-prefix {
    display: inline-block;
  }
  .input-field {
    display: inline-block;
    input {
      width: 100%;
    }
  }

  .btns {
    font-size: 0;
    position: absolute;
    top: 0;
    right: 0;
    text-align: right;
    i {
      display: block;
      font-size: 12px;
    }
  }
  .choose-management-fee-amount {
    margin-top: 5px;
    .input-field {
      vertical-align: middle;
    }
  }
}
</style>
